import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ChatStatus } from "ai";
import type { ConversationHandle } from "@/components/ai-elements/conversation";
import {
  CONVERSATION_TAIL_REENTRY_THRESHOLD_PX,
  computeShift,
  getConversationOlderMessagesRemaining,
  isConversationAtLoadedStart,
  shouldLoadOlderOnScroll,
} from "@/lib/scroll-policy";

const START_REACHED_THRESHOLD = 200;
const AUTO_FILL_MIN_ITEMS = 10;
const AUTO_FILL_RECHECK_DELAY_MS = 150;
const AUTO_FILL_MAX_RECHECKS = 3;

interface ConversationScrollItem {
  id: string;
}

/** Options for a backwards-page request. */
export interface LoadOlderOptions {
  /**
   * Recover from a stale automatic request by bypassing local in-flight
   * guards. The feed owns safely superseding the underlying network request.
   */
  force?: boolean;
}

export interface UseConversationScrollOptions<Item extends ConversationScrollItem> {
  sessionId: string;
  /** Exact rows passed to the virtualizer, in display order. */
  items: readonly Item[];
  /** The same stable row identity passed to the virtualizer. */
  getItemKey?: (item: Item, index: number) => string;
  /**
   * Optional semantic identity used to prove that rows survived a prepend
   * unchanged. JSON serialization is used by default.
   */
  getItemFingerprint?: (item: Item) => string;
  /** Render-relevant fingerprint of the final row. */
  tailContentKey: string;
  status: ChatStatus;
  /** Recovery polling serializes tail merges with prepends; live SSE does not. */
  isPolling?: boolean;
  hasLoaded: boolean;
  hasMore: boolean;
  totalMessages: number | null;
  loadedMessageCount: number;
  isLoadingOlder: boolean;
  olderPageError?: string | null;
  newerCursor?: string | null;
  isLoadingNewer?: boolean;
  newerPageError?: string | null;
  loadNewer?: () => void | Promise<void>;
  /** True only for the render transaction that commits an older page. */
  isPrepending: boolean;
  /** Monotonic token for a committed older-page prepend. */
  prependRevision: number;
  loadOlder: (options?: LoadOlderOptions) => Promise<void>;
  /** Route-owned target positioning. A null row keeps tail placement disabled while history resolves. */
  navigation?: {
    requestId: number;
    rowIndex: number | null;
    elementId: string | null;
    onPositioned?: () => void;
  } | null;
}

/**
 * Virtua-native conversation scrolling.
 *
 * There are deliberately only two positioning policies here:
 *
 * 1. `shift` owns an older-page prepend. The rows that existed before the
 *    commit must remain an unchanged suffix with stable keys.
 * 2. `scrollToIndex(last, { align: "end" })` owns initial positioning and
 *    tail changes while the reader is following the latest message.
 *
 * Released readers receive no corrective scroll writes. Virtua's item cache
 * and ResizeObserver are the single source of truth for dynamic Markdown,
 * Mermaid, and disclosure heights.
 */
export function useConversationScroll<Item extends ConversationScrollItem>({
  sessionId,
  items,
  getItemKey,
  getItemFingerprint,
  tailContentKey,
  status,
  isPolling = false,
  hasLoaded,
  hasMore,
  totalMessages,
  loadedMessageCount,
  isLoadingOlder,
  olderPageError = null,
  newerCursor = null,
  isLoadingNewer = false,
  newerPageError = null,
  loadNewer,
  isPrepending,
  prependRevision,
  loadOlder,
  navigation = null,
}: UseConversationScrollOptions<Item>) {
  const virtualizerRef = useRef<ConversationHandle>(null);
  const itemsRef = useRef(items);
  const sessionIdRef = useRef(sessionId);
  const statusRef = useRef(status);
  const isPollingRef = useRef(isPolling);
  const hasMoreRef = useRef(hasMore);
  const isLoadingOlderRef = useRef(isLoadingOlder);
  const isPrependingRef = useRef(isPrepending);
  const loadOlderRef = useRef(loadOlder);
  const pageStateRef = useRef({ olderPageError, newerCursor, isLoadingNewer, newerPageError, loadNewer });
  pageStateRef.current = { olderPageError, newerCursor, isLoadingNewer, newerPageError, loadNewer };
  const lastAutoRequestedNewerCursorRef = useRef<string | null>(null);
  const releasedNavigationRequestRef = useRef<number | null>(null);
  const followingTailRef = useRef(true);
  const userScrollActiveRef = useRef(false);
  const requestingOlderRef = useRef(false);
  const requestingOlderCountRef = useRef(0);
  const autoFillPendingCountRef = useRef<number | null>(null);
  const autoFillRafRef = useRef<number | null>(null);
  const autoFillCheckRef = useRef<(() => void) | null>(null);
  const edgeCheckRafRef = useRef<number | null>(null);
  const lastAutoRequestedFirstKeyRef = useRef<string | null>(null);
  const firstRowKeyRef = useRef<string | null>(null);
  const committedRowsRef = useRef<{
    sessionId: string;
    items: readonly Item[];
    keys: readonly string[];
  } | null>(null);
  const warnedInvalidPrependRevisionRef = useRef<number | null>(null);
  const sessionLoadGuardRef = useRef({
    sessionId,
    waitingForFreshLoad: false,
  });
  const positionedNavigationRef = useRef<string | null>(null);

  const [isAtBottom, setIsAtBottom] = useState(true);
  const [isAtTop, setIsAtTop] = useState(false);
  const [positionedSessionId, setPositionedSessionId] = useState<string | null>(null);
  const [autoFillStalledAtCount, setAutoFillStalledAtCount] = useState<number | null>(null);

  if (sessionLoadGuardRef.current.sessionId !== sessionId) {
    // Feed state resets after navigation. A true `hasLoaded` in this first
    // render still belongs to the previous session, so require the reset edge
    // before positioning the keyed Virtualizer for the new page.
    sessionLoadGuardRef.current = {
      sessionId,
      waitingForFreshLoad: hasLoaded,
    };
  } else if (!hasLoaded) {
    sessionLoadGuardRef.current.waitingForFreshLoad = false;
  }
  const hasLoadedCurrentSession = (
    hasLoaded
    && !sessionLoadGuardRef.current.waitingForFreshLoad
  );

  const rowKeys = useMemo(() => items.map((item, index) => (
    getItemKey?.(item, index) ?? item.id
  )), [getItemKey, items]);
  firstRowKeyRef.current = rowKeys[0] ?? null;
  // Keep the prepend decision stable for this data/revision transaction.
  // Geometry-only renders can happen after the layout effect records the new
  // rows; comparing against that updated snapshot would prematurely drop shift.
  const isPurePrepend = useMemo(() => {
    const committedRows = committedRowsRef.current;
    if (!isPrepending || committedRows?.sessionId !== sessionId
      || items.length <= committedRows.items.length) return false;
    const addedCount = items.length - committedRows.items.length;
    if (!committedRows.keys.every((key, previousIndex) => (
      rowKeys[addedCount + previousIndex] === key
    ))) return false;

    // A boundary tool group may retain its key while absorbing new children.
    // Shift is safe only when all previous rows are semantically unchanged.
    return committedRows.items.every((previousItem, previousIndex) => {
      const nextItem = items[addedCount + previousIndex];
      if (!nextItem) return false;
      try {
        const fingerprint = getItemFingerprint ?? JSON.stringify;
        return fingerprint(previousItem) === fingerprint(nextItem);
      } catch {
        return false;
      }
    });
  }, [getItemFingerprint, isPrepending, items, prependRevision, rowKeys, sessionId]);

  useLayoutEffect(() => {
    if (
      isPrepending
      && !isPurePrepend
      && process.env.NODE_ENV !== "production"
      && warnedInvalidPrependRevisionRef.current !== prependRevision
    ) {
      warnedInvalidPrependRevisionRef.current = prependRevision;
      console.warn(
        "[conversation] Virtua shift skipped: older-page commit was not an unchanged row-prefix prepend",
      );
    }

    committedRowsRef.current = {
      sessionId,
      items: items.slice(),
      keys: rowKeys,
    };
  }, [isPrepending, isPurePrepend, items, prependRevision, rowKeys, sessionId]);

  // Scroll callbacks can run during the commit, before effects update refs.
  itemsRef.current = items;
  sessionIdRef.current = sessionId;
  statusRef.current = status;
  isPollingRef.current = isPolling;
  hasMoreRef.current = hasMore;
  isLoadingOlderRef.current = isLoadingOlder;
  isPrependingRef.current = isPrepending;
  loadOlderRef.current = loadOlder;

  const setFollowingTail = useCallback((following: boolean) => {
    followingTailRef.current = following;
    setIsAtBottom((current) => current === following ? current : following);
  }, []);

  const scrollToTail = useCallback((smooth = false) => {
    const lastIndex = itemsRef.current.length - 1;
    if (lastIndex < 0) return false;

    virtualizerRef.current?.scrollToIndex(lastIndex, {
      align: "end",
      smooth,
    });
    return virtualizerRef.current !== null;
  }, []);

  useLayoutEffect(() => {
    followingTailRef.current = true;
    userScrollActiveRef.current = false;
    requestingOlderRef.current = false;
    requestingOlderCountRef.current = 0;
    autoFillPendingCountRef.current = null;
    lastAutoRequestedFirstKeyRef.current = null;
    warnedInvalidPrependRevisionRef.current = null;
    setAutoFillStalledAtCount(null);
    setIsAtBottom(true);
    setIsAtTop(false);
    setPositionedSessionId(null);
    positionedNavigationRef.current = null;
    lastAutoRequestedNewerCursorRef.current = null;
    releasedNavigationRequestRef.current = null;
  }, [sessionId]);

  useLayoutEffect(() => {
    lastAutoRequestedNewerCursorRef.current = null;
  }, [navigation?.requestId]);

  // Position a newly opened session exactly once. The public Virtua command
  // waits for unmeasured visible rows, so no controller-owned RAF correction
  // loop is needed.
  useEffect(() => {
    if (
      !hasLoadedCurrentSession
      || items.length === 0
      || positionedSessionId === sessionId
      || navigation !== null
    ) {
      return;
    }

    followingTailRef.current = true;
    setIsAtBottom(true);
    setPositionedSessionId(sessionId);
    scrollToTail(false);
  }, [
    hasLoadedCurrentSession,
    items.length,
    positionedSessionId,
    scrollToTail,
    sessionId,
    navigation,
  ]);

  // A URL target registers before initial placement. Keep ordinary chat
  // visible while an old-server backwards search runs, then let Virtua mount
  // the exact row before correcting to a heading inside it.
  useEffect(() => {
    if (!navigation || !hasLoadedCurrentSession || items.length === 0
      || releasedNavigationRequestRef.current === navigation.requestId) return;
    const navigationKey = `${sessionId}:${navigation.requestId}:${navigation.rowIndex}:${navigation.elementId ?? ""}`;
    if (positionedNavigationRef.current === navigationKey) return;
    followingTailRef.current = false;
    setIsAtBottom(false);
    setPositionedSessionId(sessionId);
    if (navigation.rowIndex === null) return;

    positionedNavigationRef.current = navigationKey;
    virtualizerRef.current?.scrollToIndex(navigation.rowIndex, {
      align: "start",
      smooth: false,
    });
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        if (positionedNavigationRef.current !== navigationKey
          || releasedNavigationRequestRef.current === navigation.requestId) return;
        const element = navigation.elementId
          ? document.getElementById(navigation.elementId)
          : null;
        element?.scrollIntoView({ block: "start", behavior: "auto" });
        element?.classList.add("conversation-navigation-target");
        window.setTimeout(() => element?.classList.remove("conversation-navigation-target"), 1800);
        navigation.onPositioned?.();
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame) cancelAnimationFrame(secondFrame);
    };
  }, [hasLoadedCurrentSession, items.length, navigation, sessionId]);

  // This mirrors Virtua's official chat example: appends and in-place tail
  // updates follow only while pinned. A prepend is left entirely to `shift`.
  // In particular, do not depend on the prepend flag here. Its true -> false
  // cleanup render contains no list mutation and must not get a second chance
  // to issue an end-index command after Virtua has preserved the viewport.
  useLayoutEffect(() => {
    if (
      positionedSessionId !== sessionId
      || !followingTailRef.current
      || (navigation !== null && releasedNavigationRequestRef.current !== navigation.requestId)
      || isPrependingRef.current
      || items.length === 0
    ) {
      return;
    }

    scrollToTail(false);
  }, [
    items.length,
    positionedSessionId,
    scrollToTail,
    sessionId,
    status,
    tailContentKey,
    navigation,
  ]);

  const requestOlder = useCallback(async (
    { force = false }: LoadOlderOptions = {},
  ) => {
    const capturedSessionId = sessionIdRef.current;
    const currentStatus = statusRef.current;
    if (
      currentStatus === "submitted"
      || (currentStatus === "streaming" && !isPollingRef.current)
      || !hasMoreRef.current
      || (!force && (
        isLoadingOlderRef.current
        || isPrependingRef.current
        || requestingOlderRef.current
      ))
    ) {
      return;
    }

    // From this point onward scroll events can be Virtua's shift/measurement
    // compensation. They are not evidence that the reader moved to the tail.
    userScrollActiveRef.current = false;
    requestingOlderCountRef.current += 1;
    requestingOlderRef.current = true;
    try {
      if (force) {
        await loadOlderRef.current({ force: true });
      } else {
        await loadOlderRef.current();
      }
    } finally {
      if (sessionIdRef.current === capturedSessionId) {
        requestingOlderCountRef.current = Math.max(
          0,
          requestingOlderCountRef.current - 1,
        );
        requestingOlderRef.current = requestingOlderCountRef.current > 0;
      }
    }
  }, []);

  const checkForOlderAtOffset = useCallback((offset: number) => {
    const atLoadedStart = isConversationAtLoadedStart({
      offset,
      startReachedThreshold: START_REACHED_THRESHOLD,
    });
    setIsAtTop((current) => current === atLoadedStart ? current : atLoadedStart);
    const handle = virtualizerRef.current;
    // The short-page auto-fill path owns this geometry, including its
    // no-progress guard. Do not launch a second request from the top check.
    if (!handle || handle.scrollSize <= handle.viewportSize) return;

    if (!pageStateRef.current.olderPageError && shouldLoadOlderOnScroll({
      offset,
      atBottom: followingTailRef.current,
      startReachedThreshold: START_REACHED_THRESHOLD,
      hasMore: hasMoreRef.current,
      isLoadingMore: isLoadingOlderRef.current,
      prepending: isPrependingRef.current || requestingOlderRef.current,
      streaming: (
        statusRef.current === "submitted"
        || (statusRef.current === "streaming" && !isPollingRef.current)
      ),
    })) {
      // A failed or duplicate page leaves the same first row in place. Wait
      // for another user gesture before retrying that cursor automatically.
      const firstKey = firstRowKeyRef.current;
      if (firstKey !== null && lastAutoRequestedFirstKeyRef.current !== firstKey) {
        lastAutoRequestedFirstKeyRef.current = firstKey;
        void requestOlder();
      }
    }
  }, [requestOlder]);

  // Virtua's measured end is the equivalent of an endReached callback. A
  // cursor is attempted once automatically; failures wait for the retry banner.
  const checkForNewerAtOffset = useCallback((offset: number) => {
    const handle = virtualizerRef.current;
    const page = pageStateRef.current;
    if (!handle || !page.newerCursor || !page.loadNewer
      || page.isLoadingNewer || page.newerPageError
      || isLoadingOlderRef.current || isPrependingRef.current || requestingOlderRef.current
      || handle.scrollSize - handle.viewportSize - offset > START_REACHED_THRESHOLD
      || lastAutoRequestedNewerCursorRef.current === page.newerCursor) return;
    lastAutoRequestedNewerCursorRef.current = page.newerCursor;
    // Appending history must leave the reader at the page boundary, rather
    // than following every appended page all the way to the latest message.
    setFollowingTail(false);
    void page.loadNewer();
  }, [setFollowingTail]);

  const handleScroll = useCallback((offset: number) => {
    const handle = virtualizerRef.current;
    if (!handle) return;

    // Virtua floors scrollSize at viewportSize, so this also treats a short
    // conversation as being at its only possible bottom position.
    const atPhysicalBottom = (
      offset - handle.scrollSize + handle.viewportSize
      >= -CONVERSATION_TAIL_REENTRY_THRESHOLD_PX
    );
    if (
      atPhysicalBottom
      && userScrollActiveRef.current
      && !isPrependingRef.current
      && !requestingOlderRef.current
      && !pageStateRef.current.newerCursor
    ) {
      releasedNavigationRequestRef.current = navigation?.requestId ?? null;
      setFollowingTail(true);
    }

    checkForOlderAtOffset(offset);
    checkForNewerAtOffset(offset);
  }, [checkForNewerAtOffset, checkForOlderAtOffset, navigation?.requestId, setFollowingTail]);

  const scheduleEdgeCheck = useCallback(() => {
    if (edgeCheckRafRef.current !== null) return;
    edgeCheckRafRef.current = requestAnimationFrame(() => {
      edgeCheckRafRef.current = null;
      const handle = virtualizerRef.current;
      if (handle) {
        checkForOlderAtOffset(handle.scrollOffset);
        checkForNewerAtOffset(handle.scrollOffset);
      }
    });
  }, [checkForNewerAtOffset, checkForOlderAtOffset]);

  useEffect(() => () => {
    if (edgeCheckRafRef.current !== null) {
      cancelAnimationFrame(edgeCheckRafRef.current);
      edgeCheckRafRef.current = null;
    }
  }, []);

  // Recheck both edges after loading or prepend compensation settles. Short
  // windows can need another page without producing a native scroll event.
  useEffect(() => {
    if (
      hasLoadedCurrentSession
      && positionedSessionId === sessionId
      && !isLoadingOlder
      && !isPrepending
    ) scheduleEdgeCheck();
  }, [
    hasLoadedCurrentSession,
    positionedSessionId,
    sessionId,
    isLoadingOlder,
    isPrepending,
    prependRevision,
    newerCursor,
    isLoadingNewer,
    hasMore,
    status,
    isPolling,
    scheduleEdgeCheck,
  ]);

  const handleUserScroll = useCallback(() => {
    userScrollActiveRef.current = true;
  }, []);

  const handleScrollEnd = useCallback(() => {
    userScrollActiveRef.current = false;
    scheduleEdgeCheck();
  }, [scheduleEdgeCheck]);

  const handleUserScrollAway = useCallback(() => {
    // The gesture event precedes the browser's scroll event. Release follow
    // immediately so a concurrent tail update cannot overwrite that gesture.
    handleUserScroll();
    setFollowingTail(false);
    lastAutoRequestedFirstKeyRef.current = null;
    scheduleEdgeCheck();
  }, [handleUserScroll, scheduleEdgeCheck, setFollowingTail]);

  const stickToBottom = useCallback((smooth: boolean) => {
    userScrollActiveRef.current = false;
    releasedNavigationRequestRef.current = navigation?.requestId ?? null;
    setFollowingTail(!pageStateRef.current.newerCursor);
    scrollToTail(smooth);
    // Also covers a short page which has no scroll range and emits no event.
    scheduleEdgeCheck();
  }, [navigation?.requestId, scheduleEdgeCheck, scrollToTail, setFollowingTail]);

  // Preserve the existing short-page auto-fill behavior, but base it only on
  // Virtua's measured loaded rows. No estimated unloaded-history geometry is
  // inserted into the scroll range. Virtua can initially estimate collapsed
  // tool groups from a much taller first row, so a filled-looking viewport
  // with very few render items gets a few delayed checks for ResizeObserver to
  // settle before it is treated as genuinely scrollable.
  useEffect(() => {
    if (autoFillRafRef.current !== null) {
      cancelAnimationFrame(autoFillRafRef.current);
    }

    let recheckTimer: ReturnType<typeof setTimeout> | null = null;
    let recheckCount = 0;
    let cancelled = false;

    const checkAndFill = () => {
      if (cancelled) return;
      const handle = virtualizerRef.current;
      const currentStatus = statusRef.current;
      if (
        !handle
        || !hasLoadedCurrentSession
        || positionedSessionId !== sessionId
        || !hasMoreRef.current
        || pageStateRef.current.olderPageError
        || currentStatus === "submitted"
        || (currentStatus === "streaming" && !isPollingRef.current)
      ) {
        return;
      }

      const pendingCount = autoFillPendingCountRef.current;
      if (pendingCount !== null) {
        if (loadedMessageCount > pendingCount) {
          autoFillPendingCountRef.current = null;
          setAutoFillStalledAtCount(null);
        } else if (
          isLoadingOlderRef.current
          || isPrependingRef.current
          || requestingOlderRef.current
        ) {
          return;
        } else {
          autoFillPendingCountRef.current = null;
          setAutoFillStalledAtCount(pendingCount);
          return;
        }
      }

      if (
        autoFillStalledAtCount !== null
        || isLoadingOlderRef.current
        || isPrependingRef.current
        || requestingOlderRef.current
      ) {
        return;
      }

      if (handle.scrollSize <= handle.viewportSize) {
        autoFillPendingCountRef.current = loadedMessageCount;
        void requestOlder();
        return;
      }

      if (
        items.length < AUTO_FILL_MIN_ITEMS
        && recheckCount < AUTO_FILL_MAX_RECHECKS
      ) {
        recheckCount += 1;
        recheckTimer = setTimeout(() => {
          recheckTimer = null;
          scheduleCheck();
        }, AUTO_FILL_RECHECK_DELAY_MS);
      }
    };

    const scheduleCheck = (resetRechecks = false) => {
      if (cancelled) return;

      if (resetRechecks) {
        recheckCount = 0;
        if (recheckTimer !== null) {
          clearTimeout(recheckTimer);
          recheckTimer = null;
        }
      }

      if (autoFillRafRef.current !== null) return;
      autoFillRafRef.current = requestAnimationFrame(() => {
        autoFillRafRef.current = null;
        checkAndFill();
      });
    };

    // Geometry notifications begin a fresh bounded observation window. A
    // disclosure can change DOM height after the reader has released the tail,
    // and that measurement may be the first accurate Virtua range.
    const scheduleGeometryCheck = () => scheduleCheck(true);
    autoFillCheckRef.current = scheduleGeometryCheck;
    scheduleCheck(true);

    return () => {
      cancelled = true;
      if (autoFillCheckRef.current === scheduleGeometryCheck) {
        autoFillCheckRef.current = null;
      }
      if (autoFillRafRef.current !== null) {
        cancelAnimationFrame(autoFillRafRef.current);
        autoFillRafRef.current = null;
      }
      if (recheckTimer !== null) {
        clearTimeout(recheckTimer);
        recheckTimer = null;
      }
    };
  });

  useEffect(() => {
    if (!hasMore) {
      autoFillPendingCountRef.current = null;
      setAutoFillStalledAtCount(null);
    } else if (
      autoFillStalledAtCount !== null
      && loadedMessageCount > autoFillStalledAtCount
    ) {
      setAutoFillStalledAtCount(null);
    }
  }, [autoFillStalledAtCount, hasMore, loadedMessageCount]);

  const handleGeometryResize = useCallback(() => {
    if (
      positionedSessionId !== sessionId
      || isPrependingRef.current
    ) {
      return;
    }

    // Keep tail positioning exclusive to pinned readers, but always let a
    // size change re-arm auto-fill. Expanding a collapsed tool group can cause
    // Virtua to replace inflated estimates only after this callback, even when
    // the reader is intentionally viewing older content.
    if (followingTailRef.current) {
      scrollToTail(false);
    }
    autoFillCheckRef.current?.();
    scheduleEdgeCheck();
  }, [positionedSessionId, scheduleEdgeCheck, scrollToTail, sessionId]);

  const virtualizerShift = computeShift(isPurePrepend, status, isPolling);

  const viewportBindings = useMemo(() => ({
    ref: virtualizerRef,
    bufferSize: 600,
    shift: virtualizerShift,
    onScroll: handleScroll,
    onScrollEnd: handleScrollEnd,
    onContentResize: handleGeometryResize,
    onViewportResize: handleGeometryResize,
    onUserScroll: handleUserScroll,
    onUserScrollAway: handleUserScrollAway,
  }), [
    handleGeometryResize,
    handleScroll,
    handleScrollEnd,
    handleUserScroll,
    handleUserScrollAway,
    virtualizerShift,
  ]);

  const commands = useMemo(() => ({
    loadOlder: requestOlder,
    forceLoadOlder: () => requestOlder({ force: true }),
    stickToBottom,
  }), [requestOlder, stickToBottom]);

  return {
    commands,
    isAtBottom,
    isPositioned: positionedSessionId === sessionId,
    olderMessagesRemaining: getConversationOlderMessagesRemaining({
      hasMore,
      totalMessages,
      loadedMessageCount,
    }),
    // Reaching the start always gives the reader a manual escape hatch. This
    // remains available when a collapsed row inflates Virtua's scroll range
    // enough to prevent short-page auto-fill from ever running.
    showOlderHistoryControl: hasMore && (
      isAtTop || isLoadingOlder || olderPageError !== null || autoFillStalledAtCount !== null
    ),
    viewportBindings,
  };
}
